import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { FreeCamera } from '@babylonjs/core/Cameras/freeCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import '@babylonjs/core/Layers/effectLayerSceneComponent';

import type { SimState } from '../sim/types.js';
import type { LevelDef } from '../level/types.js';
import { buildLevelMeshes, type LevelMeshes } from './platformMeshes.js';
import { createQubitMesh, type QubitMesh } from './qubitMesh.js';
import { createBackground, type Background } from './background.js';
import { createInterior, type Interior } from './interior.js';

// Chase camera placement (behind and above, looking into +Z).
const CAM_BACK = 9;
const CAM_HEIGHT = 5;
const LOOK_AHEAD = 9;
const LATERAL_FOLLOW = 0.45; // how much the camera tracks the player's X
const FOLLOW = 0.16;
const MAX_PEEK = 4;

function damp(c: number, target: number, k: number): number {
  return c + (target - c) * k;
}

export class GameScene {
  readonly engine: Engine;
  readonly scene: Scene;
  private camera: FreeCamera;
  private meshes: LevelMeshes;
  private qubit: QubitMesh;
  private background: Background;
  private peekX = 0;
  private peekAhead = 0;
  private cornerRot = 0;
  private highRot = 0;
  private interior: Interior | null = null;
  private interiorMix = 0;
  private interiorActive = false;
  private collapseTint = 0;

  constructor(engine: Engine, level: LevelDef) {
    this.engine = engine;
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.012, 0.024, 0.06, 1);

    this.camera = new FreeCamera(
      'cam',
      new Vector3(0, CAM_HEIGHT, level.spawn.z - CAM_BACK),
      this.scene,
    );
    this.camera.minZ = 0.1;
    this.camera.maxZ = 600;
    this.camera.fov = 0.9;

    const glow = new GlowLayer('glow', this.scene, { blurKernelSize: 24 });
    glow.intensity = 0.85;

    this.meshes = buildLevelMeshes(this.scene, level);
    this.qubit = createQubitMesh(this.scene);
    this.background = createBackground(this.scene);

    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  }

  private onResize(): void {
    this.engine.resize();
  }

  /** Right-stick peek: lateral nudge + look-ahead. */
  setLook(x: number, y: number): void {
    this.peekX = x * MAX_PEEK;
    this.peekAhead = -y * MAX_PEEK; // push up = look further ahead
  }

  setQubitCollapse(v: number): void {
    this.qubit.setCollapse(v);
    this.collapseTint = v;
  }

  /** Begin the mesh→SDF dissolve into the bubble time-tunnel (on scene-complete). */
  startInterior(): void {
    if (!this.interior) {
      this.interior = createInterior(this.scene, this.camera, this.engine);
      this.interior.setTint(this.collapseTint);
    }
    this.interiorActive = true;
  }

  sync(state: SimState): void {
    const p = state.player;
    this.qubit.root.position.set(p.pos.x, p.pos.y, p.pos.z);
    this.qubit.tick(state.t);

    // Chase camera: trails behind in Z, partially tracks X so the world streams past.
    const camX = p.pos.x * LATERAL_FOLLOW + this.peekX;
    const camZ = p.pos.z - CAM_BACK;
    const cam = this.camera.position;
    cam.x = damp(cam.x, camX, FOLLOW);
    cam.y = damp(cam.y, CAM_HEIGHT, FOLLOW);
    cam.z = damp(cam.z, camZ, FOLLOW);
    this.camera.setTarget(
      new Vector3(p.pos.x * LATERAL_FOLLOW, 1.4, p.pos.z + LOOK_AHEAD + this.peekAhead),
    );

    // Backdrop: far ahead, tracking the camera.
    this.background.mesh.position.set(cam.x, 8, p.pos.z + 160);

    // Puzzle visuals.
    this.meshes.gate.setEnabled(!state.complete);
    this.meshes.beam.setEnabled(state.beamActive);
    this.cornerRot = damp(this.cornerRot, state.handleCorner === 'latched' ? -0.9 : 0, 0.2);
    this.highRot = damp(this.highRot, state.handleHigh === 'latched' ? -0.9 : 0, 0.2);
    this.meshes.handleCorner.rotation.x = this.cornerRot;
    this.meshes.handleHigh.rotation.x = this.highRot;
    this.meshes.link.setEnabled(state.handleCorner === 'latched');

    const aspect = this.engine.getRenderWidth() / Math.max(1, this.engine.getRenderHeight());
    this.background.update(state.t, aspect);

    // Falling into the quantum computer: ramp the tunnel in.
    if (this.interiorActive && this.interior) {
      this.interiorMix = damp(this.interiorMix, 1, 0.025);
      this.interior.setMix(this.interiorMix);
    }
  }

  render(): void {
    this.scene.render();
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    this.scene.dispose(); // engine is shared/owned by main — do not dispose it
  }
}
